import { Backdrop, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ReactComponent as ChevronLeftIcon } from "../assets/icons/BusinessEvents/ChevronLeftIcon.svg";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import CorporateIdentity from "../components/AppointmentRequestSections/CorporateIdentity";
import ContactInformation from "../components/AppointmentRequestSections/ContactInformation";
import Location from "../components/AppointmentRequestSections/Location";
import CommercialOffer from "../components/AppointmentRequestSections/CommercialOffer";
import Documents from "../components/AppointmentRequestSections/Documents";
import Certifications from "../components/AppointmentRequestSections/Certifications";
import AppointmentRequestActions from "../components/AppointmentRequestActions";
import AppointmentScheduleModal from "../modals/AppointmentScheduleModal";
import styles from "./AppointmentRequest.module.css";

const BACKDROP_SX = {
  color: "#fff",
  zIndex: (theme) => theme.zIndex.drawer + 1,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  backdropFilter: "blur(4px)",
};

const PROGRESS_SX = { color: "#4eabe7" };
const PROGRESS_SIZE = 60;
const PROGRESS_THICKNESS = 4;

const AppointmentRequestSections = ({ request, validateField }) => (
  <div className={styles.pageContent}>
    <CorporateIdentity
      company={request}
      getFieldValidation={validateField}
      key={`corporate-identity-${request?._id || ""}`}
    />
    <ContactInformation
      company={request}
      getFieldValidation={validateField}
      key={`contact-info-${request?._id || ""}`}
    />
    <Location
      company={request}
      getFieldValidation={validateField}
      key={`location-${request?._id || ""}`}
    />
    <CommercialOffer
      company={request}
      getFieldValidation={validateField}
      key={`commercial-offer-${request?._id || ""}`}
    />
    <Documents
      company={request}
      getFieldValidation={validateField}
      key={`documents-${request?._id || ""}`}
    />
    <Certifications
      company={request}
      getFieldValidation={validateField}
      key={`certifications-${request?._id || ""}`}
    />
  </div>
);

const AppointmentRequestView = ({
  navigate,
  loading,
  request,
  user,
  validateField,
  showValidationWarning,
  isProcessing,
  isLiked,
  isScheduleModalOpen,
  eventData,
  participatingCompanyData,
  appointments,
  handleBack,
  handleRejectRequest,
  handleApproveRequest,
  handleLikeRequest,
  handleScheduleModalClose,
  handleScheduleConfirm,
}) => {
  const { t } = useTranslation();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>
        <div className={styles.backNavigation}>
          <button className={styles.backButton} onClick={handleBack}>
            <ChevronLeftIcon />
            <span>{t("common.goBack")}</span>
          </button>
        </div>
        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <h1 className={styles.title}>
              {t("pages.appointmentRequest.header")}
              {user?.nombre && (
                <span className={styles.userName}> - {user.nombre}</span>
              )}
            </h1>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        {showValidationWarning && (
          <div className={styles.subtitleSection}>
            <div className={styles.instructionsContainer}>
              <span className={styles.instructionsText}>
                {t("pages.appointmentRequest.warningSubtitle")}
              </span>
            </div>
          </div>
        )}
        {!loading && (
          <AppointmentRequestSections
            request={request}
            validateField={validateField}
          />
        )}
      </div>

      <AppointmentRequestActions
        onReject={handleRejectRequest}
        onApprove={handleApproveRequest}
        onLike={handleLikeRequest}
        loading={isProcessing}
        estado={request?.estado}
        isLiked={isLiked}
      />

      <AppointmentScheduleModal
        open={isScheduleModalOpen}
        onClose={handleScheduleModalClose}
        onConfirm={handleScheduleConfirm}
        appointmentData={request}
        eventData={eventData}
        participatingCompanyData={participatingCompanyData}
        appointments={appointments}
        user={user}
      />

      <Backdrop sx={BACKDROP_SX} open={loading || isProcessing}>
        <CircularProgress
          size={PROGRESS_SIZE}
          thickness={PROGRESS_THICKNESS}
          sx={PROGRESS_SX}
        />
      </Backdrop>
    </div>
  );
};

export default AppointmentRequestView;
